// LeadByteForm.jsx
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import SignatureCanvas from "react-signature-canvas";
import "./LeadByteForm.css"; // Ensure you have this import

// Helper to format current date/time in UK to "YYYY-MM-DD HH:MM:SS"
function getUkTimestamp() {
  const now = new Date();
  const ukTime = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/London",
    hour12: false,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(now);

  // ukTime = "dd/mm/yyyy hh:mm:ss"
  const [day, month, year, hour, minute, second] = ukTime.match(/\d+/g);
  return `${year}-${month}-${day} ${hour}:${minute}:${second}`;
}

// Generate a random IP-like string on the client (demo)
function getRandomIp() {
  const randByte = () => Math.floor(Math.random() * 256);
  return `${randByte()}.${randByte()}.${randByte()}.${randByte()}`;
}

// Full list of lender options for the dropdown
const LENDER_OPTIONS = [
  "Abarth Financial Services",
  "Abarth i-Deal",
  "Aldermore Bank",
  "Alfa Romeo Financial Services",
  "Alphera Financial Services",
  "Audi Finance",
  "Barclays Partner Finance",
  "Bentley",
  "Black Horse",
  "Blue Motor Finance",
  "BMW Finance",
  "CA Auto Finance",
  "Chrysler Financial Services",
  "Citroen Finance",
  "Close Brothers",
  "Clydesdale Financial Services",
  "Cupra",
  "DS Automobiles Finance",
  "Ducati",
  "Erwin Hymer Group Finance",
  "FCA Automotive Services UK",
  "Fiat Financial Services",
  "Firstrand Bank",
  "Ford Credit",
  "Genesis",
  "GMAC",
  "Honda Finance",
  "Hyundai Capital",
  "Jaguar Financial Services",
  "Jeep Financial Services",
  "Kia Finance",
  "Lamborghini",
  "Lancia Financial Services",
  "Land Rover Finance",
  "Lexus Financial Services",
  "Lotus Financial Services",
  "Maserati Financial Services",
  "Mazda",
  "Mercedes Finance",
  "Morgan Motor Finance",
  "MotoNovo Finance",
  "Nissan Finance",
  "Northridge Financial Services",
  "Peugeot Finance",
  "Porsche",
  "PSA Finance UK",
  "RCI Financial Services",
  "Renault Finance",
  "Santander Finance",
  "SEAT Finance",
  "Shogun Finance",
  "SKODA",
  "Stellantis Financial Services",
  "Suzuki Financial Services",
  "Toyota Financial Services",
  "UK Citroen",
  "Vauxhall Financial Services",
  "Volkswagen Finance",
  "Volvo Car Financial Services",
];

const LeadByteForm = () => {
  // If your backend is on the same server/origin, this might just be ""
  // or you can set REACT_APP_AXIOS_DOMAIN in your .env
  const axiosDomain = process.env.REACT_APP_AXIOS_DOMAIN || "";

  // State object representing our form fields
  const [formData, setFormData] = useState({
    // Required (marked with *)    
    invoiceNumber: "",  // fetched from /api/nextIds
    leadClientRef: "",  // fetched from /api/nextIds
    ipAddress: getRandomIp(),
    base64Signature: "",    
    lenderNameSelfDeclaredList: [""], // must have 1 to 5 items
    loaSignedDatetime: "",

    firstName: "",
    lastName: "",
    email: "",
    dateOfBirth: "",
    buildingName: "",
    buildingNumber: "",
    addressLine1: "",
    postCode: "",

    // Optional
    title: "",
    addressLine2: "",
    addressLine3: "",
    addressLine4: "",
    city: "",
    mobileNum: "",
    landlineNum: "",

    previous1FullName: "",
    previous1AddressLine1: "",
    previous1AddressLine2: "",
    previous1AddressLine3: "",
    previous1AddressLine4: "",
    previous1Postcode: "",

    previous2FullName: "",
    previous2AddressLine1: "",
    previous2AddressLine2: "",
    previous2AddressLine3: "",
    previous2AddressLine4: "",
    previous2Postcode: "",

    previous3FullName: "",
    previous3AddressLine1: "",
    previous3AddressLine2: "",
    previous3AddressLine3: "",
    previous3AddressLine4: "",
    previous3Postcode: "",
  });

  // For displaying the response from the backend
  const [apiResponse, setApiResponse] = useState(null);

  // Signature canvas ref
  const sigCanvasRef = useRef(null);

  // On mount, fetch next IDs from the backend
  useEffect(() => {
    axios
      .get(`${axiosDomain}/api/nextIds`)
      .then((res) => {
        const { invoiceNumber, leadClientRef } = res.data;
        setFormData((prev) => ({
          ...prev,
          invoiceNumber,
          leadClientRef,
        }));
      })
      .catch((err) => {
        console.error("Failed to fetch next IDs:", err);
        alert("Could not fetch invoice/lead IDs from the server.");
      });
    // eslint-disable-next-line
  }, []);

  // Generic handler for text/checkbox changes
  const handleChange = (e) => {
    const { name, value } = e.target;    
    setFormData((prev) => ({ ...prev, [name]: value }));    
  };

  // Lender dropdown array: allow up to 5
  const handleLenderChange = (index, e) => {
    const updatedList = [...formData.lenderNameSelfDeclaredList];
    updatedList[index] = e.target.value;
    setFormData((prev) => ({ ...prev, lenderNameSelfDeclaredList: updatedList }));
  };

  const addLender = () => {
    if (formData.lenderNameSelfDeclaredList.length < 5) {
      setFormData((prev) => ({
        ...prev,
        lenderNameSelfDeclaredList: [...prev.lenderNameSelfDeclaredList, ""],
      }));
    }
  };

  const removeLender = (index) => {
    setFormData((prev) => ({
      ...prev,
      lenderNameSelfDeclaredList: prev.lenderNameSelfDeclaredList.filter((_, i) => i !== index),
    }));
  };

  // Signature handling
  const handleSignatureEnd = () => {
    if (sigCanvasRef.current) {
      const dataURL = sigCanvasRef.current.getTrimmedCanvas().toDataURL("image/png");
      const pureBase64 = dataURL.split(",")[1] || "";
      setFormData((prev) => ({
        ...prev,
        base64Signature: pureBase64,
      }));
    }
  };

  const clearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
    setFormData((prev) => ({ ...prev, base64Signature: "" }));
  };

  // Submit form
  const handleSubmit = (e) => {
    e.preventDefault();

    // Ensure at least one lender selected
    if (
      !formData.lenderNameSelfDeclaredList.length ||
      formData.lenderNameSelfDeclaredList.every((item) => !item)
    ) {
      alert("Please select at least one lender.");
      return;
    }

    // Set LOA Signed Datetime to now
    const nowUk = getUkTimestamp();

    // Build final payload
    const payload = {
      // Required fields      
      invoiceNumber: formData.invoiceNumber,
      leadClientRef: formData.leadClientRef,
      ipAddress: formData.ipAddress,
      base64Signature: formData.base64Signature,      
      lenderNameSelfDeclaredList: formData.lenderNameSelfDeclaredList.filter(Boolean),
      loaSignedDatetime: nowUk,

      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      dateOfBirth: formData.dateOfBirth,
      buildingName: formData.buildingName,
      buildingNumber: formData.buildingNumber
        ? parseInt(formData.buildingNumber, 10) || null
        : null,
      addressLine1: formData.addressLine1,
      postCode: formData.postCode,

      // Optional
      title: formData.title || null,
      addressLine2: formData.addressLine2 || null,
      addressLine3: formData.addressLine3 || null,
      addressLine4: formData.addressLine4 || null,
      city: formData.city || null,
      mobileNum: formData.mobileNum || null,
      landlineNum: formData.landlineNum || null,

      previous1FullName: formData.previous1FullName || null,
      previous1AddressLine1: formData.previous1AddressLine1 || null,
      previous1AddressLine2: formData.previous1AddressLine2 || null,
      previous1AddressLine3: formData.previous1AddressLine3 || null,
      previous1AddressLine4: formData.previous1AddressLine4 || null,
      previous1Postcode: formData.previous1Postcode || null,

      previous2FullName: formData.previous2FullName || null,
      previous2AddressLine1: formData.previous2AddressLine1 || null,
      previous2AddressLine2: formData.previous2AddressLine2 || null,
      previous2AddressLine3: formData.previous2AddressLine3 || null,
      previous2AddressLine4: formData.previous2AddressLine4 || null,
      previous2Postcode: formData.previous2Postcode || null,

      previous3FullName: formData.previous3FullName || null,
      previous3AddressLine1: formData.previous3AddressLine1 || null,
      previous3AddressLine2: formData.previous3AddressLine2 || null,
      previous3AddressLine3: formData.previous3AddressLine3 || null,
      previous3AddressLine4: formData.previous3AddressLine4 || null,
      previous3Postcode: formData.previous3Postcode || null,
    };

    // POST to /api/leads
    axios
      .post(`${axiosDomain}/api/leads`, payload, { headers: { "Content-Type": "application/json" } })
      .then((response) => {
        setApiResponse(response.data);
        console.log("Success:", response.data);
      })
      .catch((error) => {
        console.error("Error:", error);
        alert("There was an error submitting the form.");
        setApiResponse(error.response?.data || error.message);
      });
  };

  // Render
  return (
    <div>
      <form className="leadbyte-form" onSubmit={handleSubmit}>
        <h2>Lead Submission Form</h2>

        {/* Row 1: invoiceNumber, leadClientRef, ipAddress */}
        <div className="input-group">          

          <input
            type="text"
            name="invoiceNumber"
            placeholder="Invoice Number *"
            value={formData.invoiceNumber}
            onChange={handleChange}
          />
          <input
            type="text"
            name="leadClientRef"
            placeholder="Lead Client Ref *"
            value={formData.leadClientRef}
            onChange={handleChange}
          />
          <input
            type="text"
            name="ipAddress"
            placeholder="IP Address *"
            value={formData.ipAddress}
            onChange={handleChange}
          />
        </div>

        {/* Row 2: Title, FirstName, LastName, Email */}
        <div className="input-group">
          <input
            type="text"
            name="title"
            placeholder="Title"
            value={formData.title}
            onChange={handleChange}
          />
          <input
            type="text"
            name="firstName"
            placeholder="First Name *"
            value={formData.firstName}
            onChange={handleChange}
          />
          <input
            type="text"
            name="lastName"
            placeholder="Last Name *"
            value={formData.lastName}
            onChange={handleChange}
          />
          <input
            type="email"
            name="email"
            placeholder="Email *"
            value={formData.email}
            onChange={handleChange}
          />
        </div>

        {/* Row 3: Date of Birth */}
        <div className="input-group">
          <label className="dob-label">Date of Birth *</label>
          <input
            type="date"
            name="dateOfBirth"
            value={formData.dateOfBirth}
            onChange={handleChange}
          />
        </div>

        {/* Row 4: Building Name, Building Number, AddressLine1, Address Line 2 */}
        <div className="input-group">
          <input
            type="text"
            name="buildingName"
            placeholder="Building Name *"
            value={formData.buildingName}
            onChange={handleChange}
          />
          <input
            type="text"
            name="buildingNumber"
            placeholder="Building Number *"
            value={formData.buildingNumber}
            onChange={handleChange}
          />
          <input
            type="text"
            name="addressLine1"
            placeholder="Address Line 1 *"
            value={formData.addressLine1}
            onChange={handleChange}
          />
          <input
            type="text"
            name="addressLine2"
            placeholder="Address Line 2"
            value={formData.addressLine2}
            onChange={handleChange}
          />
        </div>

        {/* Row 5: addressLine2, addressLine3, addressLine4, city */}
        <div className="input-group">
          
          <input
            type="text"
            name="addressLine3"
            placeholder="Address Line 3"
            value={formData.addressLine3}
            onChange={handleChange}
          />
          <input
            type="text"
            name="addressLine4"
            placeholder="Address Line 4"
            value={formData.addressLine4}
            onChange={handleChange}
          />
          <input
            type="text"
            name="postCode"
            placeholder="Post Code *"
            value={formData.postCode}
            onChange={handleChange}
          />
          <input
            type="text"
            name="city"
            placeholder="City"
            value={formData.city}
            onChange={handleChange}
          />
        </div>

        {/* Row 6: mobileNum, landlineNum */}
        <div className="input-group">
          <input
            type="text"
            name="mobileNum"
            placeholder="Mobile Number"
            value={formData.mobileNum}
            onChange={handleChange}
          />
          <input
            type="text"
            name="landlineNum"
            placeholder="Landline Number"
            value={formData.landlineNum}
            onChange={handleChange}
          />
        </div>

        {/* Signature */}
        <div className="signature-section">
          <h3>Signature <span style={{ color: "red" }}>*</span></h3>
          <SignatureCanvas
            ref={sigCanvasRef}
            penColor="black"
            canvasProps={{
              width: 400,
              height: 200,
              className: "sig-canvas",
            }}
            onEnd={handleSignatureEnd}
          />
          <button type="button" onClick={clearSignature}>
            Clear Signature
          </button>
          {formData.base64Signature && (
            <p>Signature captured! (Length: {formData.base64Signature.length})</p>
          )}
        </div>

        {/* Lenders: up to 5, each is a dropdown */}
        <h3>Lenders <span style={{ color: "red" }}>*</span></h3>
        {formData.lenderNameSelfDeclaredList.map((lender, index) => (
          <div key={index} className="lender-fields">
            <label>Lender {index + 1}</label>
            <div className="input-group">
              <select
                value={lender}
                onChange={(e) => handleLenderChange(index, e)}
              >
                <option value="">-- Select a Lender --</option>
                {LENDER_OPTIONS.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              {index > 0 && (
                <button type="button" onClick={() => removeLender(index)}>
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
        {formData.lenderNameSelfDeclaredList.length < 5 && (
          <button type="button" onClick={addLender}>
            Add Lender
          </button>
        )}

        {/* Previous addresses */}
        <h3>Previous Address 1</h3>
        <div className="input-group">
          <input
            type="text"
            name="previous1FullName"
            placeholder="Full Name"
            value={formData.previous1FullName}
            onChange={handleChange}
          />
          <input
            type="text"
            name="previous1AddressLine1"
            placeholder="Address Line 1"
            value={formData.previous1AddressLine1}
            onChange={handleChange}
          />
          <input
            type="text"
            name="previous1AddressLine2"
            placeholder="Address Line 2"
            value={formData.previous1AddressLine2}
            onChange={handleChange}
          />
          <input
            type="text"
            name="previous1AddressLine3"
            placeholder="Address Line 3"
            value={formData.previous1AddressLine3}
            onChange={handleChange}
          />
          <input
            type="text"
            name="previous1AddressLine4"
            placeholder="Address Line 4"
            value={formData.previous1AddressLine4}
            onChange={handleChange}
          />
          <input
            type="text"
            name="previous1Postcode"
            placeholder="Postcode"
            value={formData.previous1Postcode}
            onChange={handleChange}
          />
        </div>

        <h3>Previous Address 2</h3>
        <div className="input-group">
          <input
            type="text"
            name="previous2FullName"
            placeholder="Full Name"
            value={formData.previous2FullName}
            onChange={handleChange}
          />
          <input
            type="text"
            name="previous2AddressLine1"
            placeholder="Address Line 1"
            value={formData.previous2AddressLine1}
            onChange={handleChange}
          />
          <input
            type="text"
            name="previous2AddressLine2"
            placeholder="Address Line 2"
            value={formData.previous2AddressLine2}
            onChange={handleChange}
          />
          <input
            type="text"
            name="previous2AddressLine3"
            placeholder="Address Line 3"
            value={formData.previous2AddressLine3}
            onChange={handleChange}
          />
          <input
            type="text"
            name="previous2AddressLine4"
            placeholder="Address Line 4"
            value={formData.previous2AddressLine4}
            onChange={handleChange}
          />
          <input
            type="text"
            name="previous2Postcode"
            placeholder="Postcode"
            value={formData.previous2Postcode}
            onChange={handleChange}
          />
        </div>

        <h3>Previous Address 3</h3>
        <div className="input-group">
          <input
            type="text"
            name="previous3FullName"
            placeholder="Full Name"
            value={formData.previous3FullName}
            onChange={handleChange}
          />
          <input
            type="text"
            name="previous3AddressLine1"
            placeholder="Address Line 1"
            value={formData.previous3AddressLine1}
            onChange={handleChange}
          />
          <input
            type="text"
            name="previous3AddressLine2"
            placeholder="Address Line 2"
            value={formData.previous3AddressLine2}
            onChange={handleChange}
          />
          <input
            type="text"
            name="previous3AddressLine3"
            placeholder="Address Line 3"
            value={formData.previous3AddressLine3}
            onChange={handleChange}
          />
          <input
            type="text"
            name="previous3AddressLine4"
            placeholder="Address Line 4"
            value={formData.previous3AddressLine4}
            onChange={handleChange}
          />
          <input
            type="text"
            name="previous3Postcode"
            placeholder="Postcode"
            value={formData.previous3Postcode}
            onChange={handleChange}
          />
        </div>

        <div className="button-group">
          <button type="submit">Submit</button>
        </div>
      </form>

      {apiResponse && (
        <div className="api-response">
          <h3>API Response</h3>
          <pre>{JSON.stringify(apiResponse, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export default LeadByteForm;
